import * as React from "react";
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Input from "@amzn/awsui-components-react/polaris/input";
import Button from "@amzn/awsui-components-react/polaris/button";
import Table from "@amzn/awsui-components-react/polaris/table";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Flashbar, { FlashbarProps } from "@amzn/awsui-components-react/polaris/flashbar";
import CopyToClipboard from "@amzn/awsui-components-react/polaris/copy-to-clipboard";
import Box from "@amzn/awsui-components-react/polaris/box";
import { API } from "aws-amplify";

interface ClaimRow {
  id: string;
  type: "Claim" | "Claim Group";
}

interface State {
  personId: string;
  isLoading: boolean;
  claims: ClaimRow[];
  flashMessages: FlashbarProps.MessageDefinition[];
}

class GetAllClaimsSection extends React.Component<{}, State> {
  constructor(props: {}) {
    super(props);
    this.state = {
      personId: "",
      isLoading: false,
      claims: [],
      flashMessages: [],
    };
  }

  private handleFetch = async () => {
    const personId = this.state.personId.trim();
    if (!personId) return;

    this.setState({ isLoading: true, flashMessages: [], claims: [] });

    try {
      const response = await API.get(
        "ApaqPortalAPI",
        `/pharmacy-customer/claim/get-all-claims?personId=${encodeURIComponent(personId)}`,
        {}
      );

      const claimResponse = response?.claimResponse || response;
      const claimIds: string[] = claimResponse?.claimIds || [];
      const claimGroupIds: string[] = claimResponse?.claimGroupIds || [];

      const rows: ClaimRow[] = [
        ...claimIds.map((id: string) => ({ id, type: "Claim" as const })),
        ...claimGroupIds.map((id: string) => ({ id, type: "Claim Group" as const })),
      ];

      if (rows.length === 0) {
        this.setState({
          isLoading: false,
          flashMessages: [{ type: "info", content: "No claims found for this person ID.", dismissible: true, onDismiss: () => this.setState({ flashMessages: [] }) }],
        });
      } else {
        this.setState({ isLoading: false, claims: rows });
      }
    } catch (e) {
      const err = e as any;
      const message = err?.response?.data?.message || err?.message || "Failed to fetch claims";
      this.setState({
        isLoading: false,
        flashMessages: [{ type: "error", content: message, dismissible: true, onDismiss: () => this.setState({ flashMessages: [] }) }],
      });
    }
  };

  render() {
    const { personId, isLoading, claims, flashMessages } = this.state;

    return (
      <SpaceBetween size="l">
        {flashMessages.length > 0 && <Flashbar items={flashMessages} />}

        <Container header={<Header variant="h3">Get All Claims</Header>}>
          <form onSubmit={(e) => { e.preventDefault(); this.handleFetch(); }}>
            <SpaceBetween size="xs" direction="horizontal">
              <FormField label="Person ID">
                <div style={{ minWidth: 450 }}>
                  <Input
                    value={personId}
                    onChange={({ detail }) => this.setState({ personId: detail.value })}
                    placeholder="amzn1.actor.person.oid...."
                  />
                </div>
              </FormField>
              <FormField label="&nbsp;">
                <Button
                  variant="primary"
                  onClick={this.handleFetch}
                  loading={isLoading}
                  disabled={!personId.trim()}
                >
                  Fetch Claims
                </Button>
              </FormField>
            </SpaceBetween>
          </form>
        </Container>

        {claims.length > 0 && (
          <Container header={<Header variant="h3">Claims ({claims.length})</Header>}>
            <Table
              items={claims}
              columnDefinitions={[
                {
                  id: "type",
                  header: "Type",
                  cell: (item) => <Box>{item.type}</Box>,
                  width: 150,
                },
                {
                  id: "id",
                  header: "ID",
                  cell: (item) => (
                    <CopyToClipboard
                      copyButtonAriaLabel={`Copy ${item.id}`}
                      copySuccessText="Copied"
                      copyErrorText="Failed to copy"
                      textToCopy={item.id}
                      variant="inline"
                    />
                  ),
                },
              ]}
              variant="embedded"
              empty="No claims"
            />
          </Container>
        )}
      </SpaceBetween>
    );
  }
}

export default GetAllClaimsSection;
